import { useEffect, useMemo, useState } from 'react'
import type { DashboardResponse, DataFilters, LancamentoRow, TreeNode } from '@/domain/engineering'
import { ORIGEM_OPTIONS, avancoFonteLabel, idcPill, workStatusBadge } from '@/domain/engineering'
import { COLS_CUSTO, COLS_TENDENCIA, TreeTable } from '@/components/TreeTable'
import { CHART_COLORS, ChartPanel, ColumnChart, ComboChart, DonutChart, HBarList, LineChart } from '@/components/charts'
import { dashboardApi } from '@/lib/dashboardApi'
import { dateBR, idcText, moneyCompact, moneyFmt, numFormat, pctFmt, shortObra, toNumber, trunc } from '@/lib/format'

function Avisos({ items }: { items?: string[] }) {
  if (!items?.length) return null
  return (
    <div className="aviso-box">
      Atenção operacional
      <ul>
        {items.map(item => <li key={item}>{item}</li>)}
      </ul>
    </div>
  )
}

function EmptyPanel({ message }: { message: string }) {
  return <div className="empty-state">{message}</div>
}

function KpiCard({ title, value, sub, pill }: { title: string; value: string; sub?: string; pill?: { label: string; className: string } }) {
  return (
    <article className="kpi-card">
      <div className="kpi-head">
        <span className="kpi-title">{title}</span>
        {pill ? <span className={pill.className}>{pill.label}</span> : null}
      </div>
      <div className="kpi-value">{value}</div>
      {sub ? <div className="kpi-sub">{sub}</div> : null}
    </article>
  )
}

export function IndicadoresView({ data }: { data: DashboardResponse | null }) {
  if (!data) return <EmptyPanel message="Carregando indicadores do snapshot oficial..." />

  const kpis = data.kpis ?? {}
  const idc = idcPill(kpis.idc)
  const resultadoNum = toNumber(kpis.resultado)
  const obras = data.obrasResumo ?? []
  const etapas = data.etapas ?? []
  const mensal = data.mensal ?? []

  return (
    <section>
      <Avisos items={data.avisos} />
      <div className="kpi-grid">
        <KpiCard title="Orçado" value={moneyFmt(kpis.orcado)} sub="Custo orçado consolidado" />
        <KpiCard title="Realizado" value={moneyFmt(kpis.realizado)} sub="Realizado acumulado" />
        <KpiCard title="Comprometido" value={moneyFmt(kpis.comprometido)} sub="Pedidos e contratos" />
        <KpiCard title="Tendência" value={moneyFmt(kpis.tendencia)} sub="Projeção de encerramento" />
        <KpiCard
          title="Resultado"
          value={moneyFmt(kpis.resultado)}
          sub={kpis.resultadoPct === null || kpis.resultadoPct === undefined ? 'Sem base percentual' : pctFmt(kpis.resultadoPct)}
        />
        <KpiCard title="Estoque" value={moneyFmt(kpis.estoque)} sub={kpis.estoquePct === null || kpis.estoquePct === undefined ? undefined : pctFmt(kpis.estoquePct)} />
        <KpiCard title="IDC" value={idcText(kpis.idc)} pill={idc} sub="Índice de desempenho de custo" />
        <KpiCard
          title="Avanço físico"
          value={kpis.avancoFisico === null || kpis.avancoFisico === undefined ? '—' : pctFmt(kpis.avancoFisico > 1 ? kpis.avancoFisico : kpis.avancoFisico * 100)}
          sub={avancoFonteLabel(kpis.avancoFonte)}
        />
      </div>

      <div className="charts-row">
        <ChartPanel title="Orçado × realizado por obra" subtitle="Consolidado do snapshot ativo">
          <ColumnChart
            categories={obras.map(obra => shortObra(obra.obraNome))}
            series={[
              { name: 'Orçado', color: CHART_COLORS.navy, values: obras.map(obra => toNumber(obra.orcado)) },
              { name: 'Realizado', color: CHART_COLORS.green, values: obras.map(obra => toNumber(obra.realizado)) },
            ]}
          />
        </ChartPanel>
        <ChartPanel title="Realizado mensal" subtitle="Série do período contábil">
          <LineChart
            labels={mensal.map(ponto => ponto.mes)}
            values={mensal.map(ponto => toNumber(ponto.total))}
          />
        </ChartPanel>
      </div>

      <div className="charts-row">
        <ChartPanel title="Composição por etapa" subtitle="Maiores valores orçados">
          <DonutChart items={etapas.slice(0, 5).map(etapa => ({ label: etapa.etapa, value: toNumber(etapa.orcado) }))} />
        </ChartPanel>
        <ChartPanel title="Estoque vs. orçado" subtitle="Percentual mensal de estoque">
          <ComboChart
            labels={mensal.map(ponto => ponto.mes)}
            barName="Estoque"
            barValues={mensal.map(ponto => toNumber(ponto.estoque))}
            lineName="% estoque/orçado"
            lineValues={mensal.map(ponto => ponto.pctEstoqueOrcado)}
          />
        </ChartPanel>
      </div>

      <div className="charts-row">
        <ChartPanel title="Maiores subetapas" subtitle="Realizado acumulado">
          <HBarList
            items={(data.topSubetapas ?? []).map(item => ({ label: item.subetapa, value: toNumber(item.total) }))}
            format={moneyCompact}
            color={CHART_COLORS.blue}
          />
        </ChartPanel>
        <ChartPanel title="Estoque em subetapas" subtitle="Saldo em estoque">
          <HBarList
            items={(data.topSubetapasEstoque ?? []).map(item => ({ label: item.subetapa, value: toNumber(item.total) }))}
            format={moneyCompact}
            color={CHART_COLORS.yellow}
          />
        </ChartPanel>
      </div>

      <div className="panel panel-full" style={{ marginTop: 14 }}>
        <div className="panel-head">
          <div className="panel-title">Indicadores por obra</div>
          <div className="panel-sub">Resultado {resultadoNum >= 0 ? 'positivo' : 'negativo'} no consolidado</div>
        </div>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Obra</th>
                <th className="text-right">Orçado</th>
                <th className="text-right">Realizado</th>
                <th className="text-right">Tendência</th>
                <th className="text-right">Resultado</th>
                <th className="text-center">Avanço</th>
              </tr>
            </thead>
            <tbody>
              {obras.length ? obras.map(obra => (
                <tr key={obra.obraId}>
                  <td>{shortObra(obra.obraNome)}</td>
                  <td className="text-right">{moneyFmt(obra.orcado)}</td>
                  <td className="text-right">{moneyFmt(obra.realizado)}</td>
                  <td className="text-right">{moneyFmt(obra.tendencia)}</td>
                  <td className={`text-right ${toNumber(obra.resultado) < 0 ? 'num-negative' : 'num-positive'}`}>{moneyFmt(obra.resultado)}</td>
                  <td className="text-center">{obra.avanco === null ? '—' : pctFmt(obra.avanco > 1 ? obra.avanco : obra.avanco * 100)}</td>
                </tr>
              )) : (
                <tr><td colSpan={6}><EmptyPanel message="Nenhuma obra no snapshot publicado." /></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}

export function DetalheView({ data, filters }: { data: DashboardResponse | null; filters: DataFilters }) {
  const [busca, setBusca] = useState('')
  const [origem, setOrigem] = useState('')
  const [offset, setOffset] = useState(0)
  const [rows, setRows] = useState<LancamentoRow[]>([])
  const [total, setTotal] = useState(0)
  const [limit, setLimit] = useState(50)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setOffset(0)
  }, [filters.obraId, filters.unidadeId, filters.inicio, filters.fim, busca, origem])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    void dashboardApi.detail({ ...filters, limit: 50, offset, busca, origem }).then((page) => {
      if (cancelled) return
      setRows(page?.rows ?? [])
      setTotal(page?.total ?? 0)
      setLimit(page?.limit || 50)
    }).catch((reason) => {
      if (cancelled) return
      setError(reason instanceof Error ? reason.message : 'Falha ao carregar o detalhe contábil.')
      setRows([])
      setTotal(0)
    }).finally(() => {
      if (!cancelled) setLoading(false)
    })
    return () => { cancelled = true }
  }, [filters, offset, busca, origem])

  const pageStart = total === 0 ? 0 : offset + 1
  const pageEnd = Math.min(offset + limit, total)

  return (
    <section>
      <Avisos items={data?.avisos} />
      <div className="toolbar">
        <div className="search-box">
          <span className="search-icon">⌕</span>
          <input className="text-input" value={busca} onChange={event => setBusca(event.target.value)} placeholder="Buscar documento, credor ou insumo" />
        </div>
        <select value={origem} onChange={event => setOrigem(event.target.value)}>
          {ORIGEM_OPTIONS.map(option => <option key={option.value || 'todas'} value={option.value}>{option.label}</option>)}
        </select>
        {loading ? <span className="loading-inline"><span className="spinner" /> Carregando lançamentos...</span> : null}
      </div>
      {error ? <div className="aviso-box">{error}</div> : null}
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Obra</th>
              <th>WBS</th>
              <th>Insumo</th>
              <th>Documento</th>
              <th>Credor</th>
              <th className="text-center">Origem</th>
              <th className="text-right">Valor</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? rows.map((row, index) => (
              <tr key={`${row.documento}-${row.insumoCodigo}-${index}`}>
                <td className="mono">{dateBR(row.dataContabil)}</td>
                <td>{shortObra(row.obraNome)}</td>
                <td className="mono">{orDashSafe(row.wbsCodigo)}</td>
                <td title={row.insumoDescricao}>{trunc(`${row.insumoCodigo} ${row.insumoDescricao}`.trim(), 48)}</td>
                <td className="mono">{orDashSafe(row.documento)}</td>
                <td>{trunc(row.credor, 32)}</td>
                <td className="col-origin"><span className={`origin-badge ${row.origem?.toLowerCase() || ''}`}>{row.origem || '—'}</span></td>
                <td className="text-right">{moneyFmt(row.valor)}</td>
              </tr>
            )) : (
              <tr><td colSpan={8}><EmptyPanel message="Nenhum lançamento encontrado para o filtro atual." /></td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="pagination">
        <span className="pagination-info">{total ? `${pageStart}–${pageEnd} de ${numFormat(total)} lançamentos` : '0 lançamentos'}</span>
        <div className="pagination-buttons">
          <button type="button" className="btn-page" disabled={offset <= 0 || loading} onClick={() => setOffset(prev => Math.max(0, prev - limit))}>Anterior</button>
          <button type="button" className="btn-page" disabled={offset + limit >= total || loading} onClick={() => setOffset(prev => prev + limit)}>Próxima</button>
        </div>
      </div>
    </section>
  )
}

function orDashSafe(value?: string | null) {
  return value && value.trim() ? value : '—'
}

export function CustoNivelView({ nodes, avisos, filters }: { nodes: TreeNode[]; avisos: string[]; filters: DataFilters }) {
  const [selected, setSelected] = useState<TreeNode | null>(null)
  const [insumoRows, setInsumoRows] = useState<LancamentoRow[]>([])
  const [insumoTotal, setInsumoTotal] = useState(0)
  const [insumoError, setInsumoError] = useState('')
  const [loadingInsumos, setLoadingInsumos] = useState(false)

  useEffect(() => {
    if (!selected) {
      setInsumoRows([])
      setInsumoTotal(0)
      return
    }
    let cancelled = false
    setLoadingInsumos(true)
    setInsumoError('')
    void dashboardApi.insumos({ ...filters, codigo: selected.codigo, termo: selected.nome }).then((result) => {
      if (cancelled) return
      setInsumoRows(result?.rows ?? [])
      setInsumoTotal(result?.total ?? 0)
    }).catch((reason) => {
      if (cancelled) return
      setInsumoError(reason instanceof Error ? reason.message : 'Falha ao carregar insumos da WBS.')
      setInsumoRows([])
      setInsumoTotal(0)
    }).finally(() => {
      if (!cancelled) setLoadingInsumos(false)
    })
    return () => { cancelled = true }
  }, [selected, filters])

  return (
    <section>
      <Avisos items={avisos} />
      <TreeTable
        nodes={nodes}
        columns={COLS_CUSTO}
        searchPlaceholder="Buscar código ou descrição da WBS"
        infoText="Clique em um nível para ver insumos"
        selectedId={selected?.id ?? null}
        onSelect={setSelected}
      />
      <div className="panel insumos-panel">
        <div className="panel-head">
          <div className="panel-title">{selected ? `Insumos · ${selected.codigo} ${selected.nome}` : 'Insumos da WBS'}</div>
          <div className="panel-sub">{selected ? `${numFormat(insumoTotal)} registros` : 'Selecione um nó da árvore para detalhar insumos.'}</div>
        </div>
        {insumoError ? <div className="aviso-box">{insumoError}</div> : null}
        {loadingInsumos ? <div className="loading-inline"><span className="spinner" /> Carregando insumos...</div> : null}
        {selected ? (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Insumo</th>
                  <th>Credor</th>
                  <th>Documento</th>
                  <th className="text-center">Origem</th>
                  <th className="text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {insumoRows.length ? insumoRows.map((row, index) => (
                  <tr key={`${row.insumoCodigo}-${row.documento}-${index}`}>
                    <td title={row.insumoDescricao}>{trunc(`${row.insumoCodigo} ${row.insumoDescricao}`.trim(), 52)}</td>
                    <td>{trunc(row.credor, 28)}</td>
                    <td className="mono">{orDashSafe(row.documento)}</td>
                    <td className="col-origin"><span className={`origin-badge ${row.origem?.toLowerCase() || ''}`}>{row.origem || '—'}</span></td>
                    <td className="text-right">{moneyFmt(row.valor)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={5}><EmptyPanel message="Nenhum insumo neste nível para o filtro atual." /></td></tr>
                )}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </section>
  )
}

export function TendenciaView({ data, nodes, avisos }: { data: DashboardResponse | null; nodes: TreeNode[]; avisos: string[] }) {
  const desvios = useMemo(() => {
    const positivos = data?.desviosPositivos ?? []
    const negativos = data?.desviosNegativos ?? []
    return { positivos, negativos }
  }, [data])

  return (
    <section>
      <Avisos items={[...(avisos ?? []), ...(data?.avisos ?? [])]} />
      <div className="charts-row">
        <ChartPanel title="Maiores economias" subtitle="Etapas com resultado positivo">
          <HBarList
            items={desvios.positivos.map(item => ({ label: item.etapa, value: toNumber(item.resultado) }))}
            color={CHART_COLORS.green}
            format={moneyCompact}
          />
        </ChartPanel>
        <ChartPanel title="Maiores estouros" subtitle="Etapas com resultado negativo">
          <HBarList
            items={desvios.negativos.map(item => ({ label: item.etapa, value: Math.abs(toNumber(item.resultado)) }))}
            color={CHART_COLORS.red}
            format={moneyCompact}
          />
        </ChartPanel>
      </div>
      <div className="panel" style={{ marginBottom: 14 }}>
        <div className="panel-head">
          <div className="panel-title">Status orçamentário por obra</div>
        </div>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Obra</th>
                <th className="text-right">Orçado</th>
                <th className="text-right">Tendência</th>
                <th className="text-right">Resultado</th>
                <th className="text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {(data?.obrasResumo ?? []).length ? (data?.obrasResumo ?? []).map((obra) => {
                const desvioPct = toNumber(obra.orcado) === 0 ? null : (toNumber(obra.resultado) / toNumber(obra.orcado)) * 100
                const badge = workStatusBadge(desvioPct)
                return (
                  <tr key={obra.obraId}>
                    <td>{shortObra(obra.obraNome)}</td>
                    <td className="text-right">{moneyFmt(obra.orcado)}</td>
                    <td className="text-right">{moneyFmt(obra.tendencia)}</td>
                    <td className={`text-right ${toNumber(obra.resultado) < 0 ? 'num-negative' : 'num-positive'}`}>{moneyFmt(obra.resultado)}</td>
                    <td className="text-center">{badge ? <span className={badge.className}>{badge.label}</span> : '—'}</td>
                  </tr>
                )
              }) : (
                <tr><td colSpan={5}><EmptyPanel message="Sem dados de tendência no snapshot." /></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <TreeTable
        nodes={nodes}
        columns={COLS_TENDENCIA}
        searchPlaceholder="Buscar WBS na tendência"
        infoText="Tendência = realizado + estoque + saldo + a comprometer"
      />
    </section>
  )
}
